import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { 
  PlusIcon,
  ListBulletIcon
} from '@heroicons/react/24/outline';

import { savedAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import SavedListModal from '../components/SavedListModal';

const SavedPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Modal states
  const [listModal, setListModal] = useState({ isOpen: false, list: null, mode: 'create' });

  // Intersection observer ref for infinite scroll
  const observerTarget = useRef(null);

  // Build query parameters for API call
  const buildQueryParams = (pageParam = 0) => {
    return {
      offset: pageParam,
      limit: 20,
      include_items: false
    };
  };

  // Fetch saved lists with infinite query
  const { 
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch 
  } = useInfiniteQuery({
    queryKey: ['saved-lists-page'],
    queryFn: ({ pageParam = 0 }) => savedAPI.getLists(buildQueryParams(pageParam)),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const pagination = lastPage?.data?.data?.pagination;
      const lastPageLists = lastPage?.data?.data?.lists || [];
      const requestedLimit = 20;
      
      if (lastPageLists.length < requestedLimit) {
        return undefined;
      }
      
      if (pagination && pagination.hasMore === false) {
        return undefined;
      }
      
      if (pagination && pagination.hasMore === true && lastPageLists.length === requestedLimit) {
        const totalLists = allPages.reduce((total, page) => total + (page?.data?.data?.lists?.length || 0), 0);
        return totalLists;
      }
      
      return undefined;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!user
  });

  // Flatten all pages into a single lists array
  const savedLists = data?.pages.reduce((acc, page) => {
    return [...acc, ...(page?.data?.data?.lists || [])];
  }, []) || [];



  // Intersection Observer for infinite scroll
  const observeLastList = useCallback(
    (node) => {
      if (isLoading || isFetchingNextPage) return;
      if (observerTarget.current) observerTarget.current.disconnect();
      
      observerTarget.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      }, {
        threshold: 0.1, // Use threshold for more reliable triggering
      });
      
      if (node) observerTarget.current.observe(node);
    },
    [isLoading, isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  // Handle view mode switch
  const handleViewList = (list) => {
    navigate(`/saved/${list.id}`, {
      state: {
        previousPath: '/saved',
        transitionDirection: 'forward'
      }
    });
  };


  // Handle list actions
  const handleCreateList = () => {
    setListModal({ isOpen: true, list: null, mode: 'create' });
  };

  const closeListModal = () => {
    setListModal({ isOpen: false, list: null, mode: 'create' });
  };


  if (!user) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Please log in to view your saved lists
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            You need to be logged in to manage your favorites.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto lg:px-8 pt-2 pb-0 pt-4">
          {/* Top bar with title and view toggle */}
          <div className="flex items-center justify-between mb-2 px-4 md:px-0">
            <div className="flex items-center gap-4">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Saved
              </h1>
            </div>

          </div>

        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-0 md:px-6 lg:px-8 py-2">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, index) => (
                <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden animate-pulse">
                  <div className="w-full h-48 bg-gray-300 dark:bg-gray-600"></div>
                  <div className="p-4">
                    <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded mb-2"></div>
                    <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-600 dark:text-red-400 mb-4">
                Failed to load saved lists
                <br />
                <small className="text-sm">Error: {error.message}</small>
                {error.response?.status === 401 && (
                  <div className="mt-2 text-sm">
                    <strong>Please log in to view your saved lists</strong>
                  </div>
                )}
              </div>
              <button
                onClick={() => refetch()}
                className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : (
            <>
              {(
                /* Lists View */
                savedLists.length === 0 ? (
                  <div className="space-y-0">
                    {/* Create New List Button - Always Visible */}
                    <div className="mb-6 border-y border-gray-200 dark:border-gray-700">
                      <button
                        onClick={handleCreateList}
                        className="w-full flex items-center gap-4 p-4 hover:bg-gray-100 dark:hover:bg-gray-800 text-left"
                      >
                        <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0">
                          <PlusIcon className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                        </div>
                        <div>
                          <div className="text-lg font-medium text-gray-900 dark:text-white">
                            Create new list
                          </div>
                        </div>
                      </button>
                    </div>

                    {/* Empty State Message */}
                    <div className="text-center py-8">
                      <ListBulletIcon className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No lists yet</h3>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Create New List Button */}
                    <div className="border-y border-gray-200 dark:border-gray-700">
                      <button
                        onClick={handleCreateList}
                        className="w-full flex items-center gap-4 p-4 hover:bg-gray-100 dark:hover:bg-gray-800 text-left"
                      >
                        <div className="w-12 h-12 bg-gray-700 dark:bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                          <PlusIcon className="w-6 h-6 text-gray-50 dark:text-gray-600" />
                        </div>
                        <div>
                          <div className="text-lg font-medium text-gray-900 dark:text-white">
                            Create new list
                          </div>
                        </div>
                      </button>
                    </div>

                    {/* Lists */}
                    <div className="space-y-0">
                      {savedLists.map((list, index) => {
                        const isNearEnd = index >= savedLists.length - 3;
                        
                        return (
                          <div
                            key={list.id}
                            ref={isNearEnd ? observeLastList : null}
                            className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-shadow duration-200"
                          >
                            <div className="flex items-center justify-between p-4">
                              <button
                                onClick={() => handleViewList(list)}
                                className="flex items-center gap-4 flex-1 text-left min-w-0 overflow-hidden"
                              >
                                <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-xl flex items-center justify-center flex-shrink-0">
                                  <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                                  </svg>
                                </div>
                                <div className="flex-1 min-w-0 overflow-hidden">
                                  <div className="flex items-center gap-2 mb-1 min-w-0">
                                    <h3 className="text-lg font-medium text-gray-900 dark:text-white truncate flex-1 min-w-0" title={list.name}>
                                      {list.name}
                                    </h3>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      {list.isDefault && (
                                        <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded whitespace-nowrap">
                                          Default
                                        </span>
                                      )}
                                      {!list.isPublic && (
                                        <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 min-w-0">
                                    <span className="flex-shrink-0">{list.itemCount || 0} item{(list.itemCount || 0) !== 1 ? 's' : ''}</span>
                                    {list.description && (
                                      <span className="truncate flex-1 min-w-0" title={list.description}>{list.description}</span>
                                    )}
                                  </div>
                                </div>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Infinite scroll loading spinner */}
                    {isFetchingNextPage && (
                      <div className="flex justify-center items-center py-8">
                        <div className="flex items-center space-x-2">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 dark:border-blue-400"></div>
                          <span className="text-gray-600 dark:text-gray-400 text-sm">Loading more lists...</span>
                        </div>
                      </div>
                    )}
                  </>
                )
              )}
            </>
          )}
        </div>
      </div>

      {/* List Modal */}
      <SavedListModal
        isOpen={listModal.isOpen}
        onClose={closeListModal}
        list={listModal.list}
        mode={listModal.mode}
      />

    </div>
  );
};

export default SavedPage;
